/**
 * ErrorBoundary - Fanger opp uventede feil i React-komponenter
 * Viser en profesjonell feilmelding i stedet for hvit skjerm
 * @version 1.0.0
 */

import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log til konsoll kun i development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary fanget feil:', error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { fallback, title = 'Noe gikk galt', compact = false } = this.props;

      // Custom fallback
      if (fallback) {
        return fallback;
      }

      // Kompakt visning for inline-komponenter
      if (compact) {
        return (
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-red-300">{title}</span>
            <button
              onClick={this.handleRetry}
              className="ml-auto px-2 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded transition-colors"
            >
              Prøv igjen
            </button>
          </div>
        );
      }

      // Full visning
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          
          <h2 className="text-xl font-bold text-[var(--text-main)] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {title}
          </h2>
          
          <p className="text-[var(--text-muted)] mb-6 max-w-md">
            En uventet feil oppstod. Du kan prøve å laste komponenten på nytt, 
            eller gå tilbake til forsiden.
          </p>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <div className="w-full max-w-lg mb-6 p-3 bg-[var(--bg-highlight)] border border-[var(--border-subtle)] rounded-lg text-left">
              <p className="text-xs text-red-400 font-mono break-all">
                {this.state.error.toString()}
              </p>
            </div>
          )}
          
          <div className="flex gap-3">
            <button
              onClick={this.handleRetry}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--copper)] hover:bg-[var(--copper-dark)] text-white rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Prøv igjen
            </button>
            
            <button
              onClick={this.handleGoHome}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-highlight)] hover:bg-[var(--bg-card)] text-[var(--text-main)] border border-[var(--border-subtle)] rounded-lg transition-colors"
            >
              <Home className="w-4 h-4" />
              Til forsiden
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
